// src/components/SocioCard.jsx
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { FileTextIcon } from "lucide-react";
import ModalDocumentos from "./ModalDocumentos";
import { borrarSocio } from "../services/sociosApi";
import { authFetch, isEmpleado } from "../services/auth";

// ---- Concurrencia limitada y cache checklist (por sesión) ----
const CHECKLIST_MAX_CONCURRENCY = 4;
if (!window.__CHECKLIST_SEM) {
  window.__CHECKLIST_SEM = { running: 0, q: [] };
}
async function runLimited(fn) {
  const sem = window.__CHECKLIST_SEM;
  if (sem.running >= CHECKLIST_MAX_CONCURRENCY) {
    await new Promise((res) => sem.q.push(res));
  }
  sem.running++;
  try { return await fn(); }
  finally {
    sem.running--;
    const next = sem.q.shift();
    if (next) next();
  }
}
const checklistCache = {
  get(id) {
    try {
      const raw = sessionStorage.getItem(`chk:${id}`);
      if (!raw) return null;
      const { t, v } = JSON.parse(raw);
      if (Date.now() - t > 5 * 60 * 1000) return null; // TTL 5 min
      return v;
    } catch { return null; }
  },
  set(id, v) {
    try { sessionStorage.setItem(`chk:${id}`, JSON.stringify({ t: Date.now(), v })); } catch {}
  },
};

// ---- Persistencia local de bajas (por si el backend no devuelve el cambio todavía) ----
const bajasLocal = {
  get(id) { try { return JSON.parse(localStorage.getItem(`baja:${id}`) || "null"); } catch { return null; } },
  set(id, fechaStr) { try { localStorage.setItem(`baja:${id}`, JSON.stringify({ fecha: fechaStr || null, at: Date.now() })); } catch {} },
  clear(id) { try { localStorage.removeItem(`baja:${id}`); } catch {} },
};

/* ---------------- helpers aura (mismo criterio que SocioPanel) --------------- */
function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const target = new Date(dateStr + "T00:00:00");
  const now = new Date();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}
function getAlertLevelFromResumen(resumen) {
  if (!resumen?.proximas_caducidades?.length) return "none";
  const soonest = Math.min(
    ...resumen.proximas_caducidades.map((d) => daysUntil(d.fecha_caducidad)).filter((n) => Number.isFinite(n))
  );
  if (!Number.isFinite(soonest)) return "none";
  if (soonest <= 7) return "high";
  if (soonest <= 30) return "med";
  return "none";
}
function normalizeAlert(v) {
  let val = String(v ?? "none").toLowerCase();
  if (val === "alto") val = "high";
  if (val === "media" || val === "medio") val = "med";
  return ["high", "med", "none"].includes(val) ? val : "none";
}

/* ------------------------ status visual (chip) ------------------------ */
function computeSocioStatus({ fecha_baja, enTramiteFlag, pendientesNum, estadoSrv }) {
  const isBajaByEstado = String(estadoSrv || "").toUpperCase().includes("BAJA");
  if (fecha_baja || isBajaByEstado) {
    return { key: "baja", label: fecha_baja ? `Baja · ${fmt(fecha_baja)}` : "Baja", chipClass: "bg-red-100 text-red-800", dotClass: "bg-red-600" };
  }
  if (enTramiteFlag || (typeof pendientesNum === "number" && pendientesNum > 0)) {
    return { key: "tramite", label: "En trámite", chipClass: "bg-indigo-100 text-indigo-800", dotClass: "bg-indigo-600" };
  }
  return { key: "activo", label: "Activo", chipClass: "bg-green-100 text-green-800", dotClass: "bg-green-600" };
}
function fmt(s) {
  try {
    const d = new Date(s + "T00:00:00");
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy}`;
  } catch { return s || "—"; }
}

/* ======== Heurísticas robustas para interpretar el checklist ======== */
function extractChecklistItems(payload) {
  const items = [];
  function visit(node) {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) { for (const el of node) visit(el); return; }
    if (Array.isArray(node.items)) {
      for (const it of node.items) {
        if (it && typeof it === "object") {
          items.push({
            estado: (it.estado ?? it.state ?? it.status ?? "").toString().toLowerCase(),
            caducidad: it.caducidad ?? it.fecha_caducidad ?? it.expira ?? null,
            requiere_caducidad: !!(it.requiere_caducidad ?? it.requiereCaducidad ?? it.require_expiry ?? it.requireExpiry ?? false),
          });
        }
      }
    }
    const childKeys = ["blocks","sections","grupos","groups","children","subitems","subItems","nodes"];
    for (const k of childKeys) if (Array.isArray(node[k])) visit(node[k]);
    for (const [k, v] of Object.entries(node)) {
      if (v && typeof v === "object" && !Array.isArray(v) && !childKeys.includes(k) && k !== "items") visit(v);
    }
  }
  visit(payload);
  return items;
}

function computePendientesFromPayload(payload) {
  const sum = payload?.summary ?? payload?.resumen ?? null;
  if (sum) {
    const raw = sum.pendientes_total ?? sum.pendientes ?? sum.todo ?? null;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  if (payload && payload.total != null && (payload.completados != null || payload.hechos != null || payload.done != null)) {
    const done = Number(payload.completados ?? payload.hechos ?? payload.done);
    const total = Number(payload.total);
    if (Number.isFinite(done) && Number.isFinite(total)) return Math.max(0, total - done);
  }
  const items = extractChecklistItems(payload);
  if (items.length === 0) return 1; // vacío ⇒ en trámite
  const OK = new Set(["subido","ok","completo","cargado","vigente","cargado_ok","valido","validado","aprobado","aprobada","aceptado","aceptada"]);
  let pendientes = 0;
  for (const it of items) {
    const st = (it.estado || "").trim().toLowerCase();
    const requiereCad = !!it.requiere_caducidad;
    const sinCad = requiereCad && !it.caducidad;
    const esOk = OK.has(st) && !sinCad;
    if (!esOk) pendientes++;
  }
  return pendientes;
}

/* ===================== Componente ===================== */
const SocioCard = ({
  id, nombre, matricula, imagen, estado, fecha_baja,
  enTramite, pendientes, onDeleted, onEdit, onChanged,
  alertLevel, prefetch = "visible",
  onChecklistSummaryChange,   // ← NUEVO (opcional)
  onHydratedStatus,           // ← NUEVO (opcional)
}) => {
  /* permisos / UI local */
  const puedeAdministrar = useMemo(() => isEmpleado(), []);
  const [modalOpen, setModalOpen] = useState(false);

  // menú + eliminar
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [removing, setRemoving] = useState(false);
  const menuRef = useRef(null);

  // baja/reactivación
  const [bajaModalOpen, setBajaModalOpen] = useState(false);
  const [bajaDateInput, setBajaDateInput] = useState("");
  const [savingBaja, setSavingBaja] = useState(false);

  // estado interno
  const [serverFechaBaja, setServerFechaBaja] = useState(fecha_baja || null);
  const [serverEstado, setServerEstado] = useState(estado || "");

  // baja local
  useEffect(() => {
    const loc = bajasLocal.get(id);
    if (loc && !serverFechaBaja && !fecha_baja && !String(serverEstado).toUpperCase().includes("BAJA")) {
      setServerFechaBaja(loc.fecha || null);
      setServerEstado("BAJA");
      onHydratedStatus?.({ id, fecha_baja: loc.fecha || null, estado: "BAJA" }); // ← AVISA
    }
    if (fecha_baja || String(estado).toUpperCase().includes("BAJA")) {
      bajasLocal.clear(id);
    }
  }, [id, fecha_baja, estado, serverFechaBaja, serverEstado, onHydratedStatus]);

  const [internalAlert, setInternalAlert] = useState("none");
  const [internalPend, setInternalPend] = useState(typeof pendientes === "number" ? pendientes : null);
  const [internalTramite, setInternalTramite] = useState(enTramite != null ? !!enTramite : null);

  // sync props
  useEffect(() => { if (fecha_baja != null && fecha_baja !== "") setServerFechaBaja(fecha_baja); }, [fecha_baja]);
  useEffect(() => { if (estado != null && estado !== "") setServerEstado(estado); }, [estado]);

  // visibilidad
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(prefetch === "instant");
  const hydratedRef = useRef(false);

  /* aura halo */
  useEffect(() => {
    if (typeof alertLevel !== "undefined") return;
    let alive = true;
    authFetch(`/api/socios/${id}/resumen`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((resumen) => { if (alive) setInternalAlert(getAlertLevelFromResumen(resumen)); })
      .catch(() => { if (alive) setInternalAlert("none"); });
    return () => { alive = false; };
  }, [id, alertLevel]);
  const level = normalizeAlert(typeof alertLevel !== "undefined" ? alertLevel : internalAlert);

  /* lazy-hydrate cuando visible (observer en contenedor) */
  useEffect(() => {
    if (prefetch !== "visible") return;
    const el = cardRef.current;
    if (!el) { setVisible(true); return; }
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e && e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { rootMargin: "200px" });
    obs.observe(el);
    const t = setTimeout(() => setVisible((v) => v || true), 400);
    return () => { clearTimeout(t); obs.disconnect(); };
  }, [prefetch]);

  useEffect(() => {
    const knowBaja = !!(serverFechaBaja || fecha_baja);
    const knowTramite = internalTramite != null || typeof pendientes === "number" || enTramite != null;
    if (!visible || hydratedRef.current) return;
    hydratedRef.current = true;

    const stagger = setTimeout(() => {
      const ctrl1 = new AbortController();
      const ctrl2 = new AbortController();

      // 1) Hidratar BAJA
      if (!knowBaja) {
        runLimited(() => authFetch(`/api/socios/${id}`, { signal: ctrl1.signal }))
          .then((r) => (r.ok ? r.json() : null))
          .then((json) => {
            if (!json) return;
            const bajaSrv = json.fecha_baja ?? json.fechaBaja ?? json.baja_fecha ?? null;
            const estadoSrv = json.estado ?? json.status ?? "";
            setServerFechaBaja(bajaSrv);
            setServerEstado(estadoSrv);
            // avisa al padre del estado hidratado
            onHydratedStatus?.({ id, fecha_baja: bajaSrv ?? null, estado: estadoSrv ?? "" });

            if (!bajaSrv && !String(estadoSrv).toUpperCase().includes("BAJA")) {
              const loc = bajasLocal.get(id);
              if (loc) {
                setServerFechaBaja(loc.fecha || null);
                setServerEstado("BAJA");
                onHydratedStatus?.({ id, fecha_baja: loc.fecha || null, estado: "BAJA" });
              }
            } else {
              if (bajaSrv || String(estadoSrv).toUpperCase().includes("BAJA")) bajasLocal.clear(id);
            }
          })
          .catch(() => {});
      }

      // 2) Hidratar “En trámite”
      if (!knowTramite) {
        (async () => {
          const cached = checklistCache.get(id);
          if (cached != null) {
            setInternalPend(cached);
            setInternalTramite(cached > 0);
            onChecklistSummaryChange?.(id, cached);
            return;
          }

          const markPending = () => {
            checklistCache.set(id, 1);
            setInternalPend(1);
            setInternalTramite(true);
            onChecklistSummaryChange?.(id, 1);
          };

          try {
            const fetchOne = (url) => runLimited(() => authFetch(url, { signal: ctrl2.signal }));
            let r = await fetchOne(`/api/socios/${id}/checklist`);
            if (!r.ok) r = await fetchOne(`/api/socios/${id}/checklist?summary=1`);
            if (!r.ok) { markPending(); return; }

            const payload = await r.json();
            let pendientesCount = 0;

            if (Array.isArray(payload?.blocks)) {
              const items = payload.blocks.flatMap((b) => Array.isArray(b.items) ? b.items : []);
              if (items.length === 0) pendientesCount = 1;
              else {
                const OK = new Set(["subido","ok","completo","cargado","vigente","cargado_ok","valido","validado","aprobado","aprobada","aceptado","aceptada"]);
                pendientesCount = items.reduce((acc, it) => {
                  const st = String(it?.estado ?? it?.state ?? it?.status ?? "").trim().toLowerCase();
                  const requiereCad = !!(it?.requiere_caducidad ?? it?.requiereCaducidad ?? it?.require_expiry ?? it?.requireExpiry ?? false);
                  const sinCad = requiereCad && !it?.caducidad && !it?.fecha_caducidad;
                  const esOk = OK.has(st) && !sinCad;
                  return acc + (esOk ? 0 : 1);
                }, 0);
              }
            } else if (payload?.summary || payload?.resumen || payload) {
              let n = Number(
                (payload.summary?.pendientes_total ?? payload.summary?.pendientes ?? payload.summary?.todo ??
                 payload.resumen?.pendientes_total ?? payload.resumen?.pendientes ?? payload.resumen?.todo)
              );
              if (!Number.isFinite(n) && payload.total != null) {
                const done = Number(payload.completados ?? payload.hechos ?? payload.done);
                const total = Number(payload.total);
                if (Number.isFinite(done) && Number.isFinite(total)) n = Math.max(0, total - done);
              }
              if (!Number.isFinite(n)) n = 1;
              pendientesCount = n;
            } else {
              pendientesCount = 1;
            }

            checklistCache.set(id, pendientesCount);
            setInternalPend(pendientesCount);
            setInternalTramite(pendientesCount > 0);
            onChecklistSummaryChange?.(id, pendientesCount);
          } catch {
            markPending();
          }
        })();
      }

      return () => { ctrl1?.abort?.(); ctrl2?.abort?.(); };
    }, 80);

    return () => clearTimeout(stagger);
  }, [visible, id, enTramite, pendientes, fecha_baja, serverFechaBaja, internalTramite, onChecklistSummaryChange, onHydratedStatus]);

  /* cerrar menú al click fuera */
  useEffect(() => {
    function onDocClick(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  /* acciones baja */
  const marcarBaja = useCallback(async (fecha) => {
    if (!fecha) return;
    setSavingBaja(true);
    try {
      const body = { fecha_baja: fecha, fechaBaja: fecha, en_baja: true, enBaja: true, estado: "BAJA", activo: false };
      const r = await authFetch(`/api/socios/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error("No se pudo marcar la baja");
      bajasLocal.set(id, fecha);
      const r2 = await authFetch(`/api/socios/${id}`);
      if (r2.ok) {
        const j = await r2.json();
        const fb = j.fecha_baja ?? j.fechaBaja ?? j.baja_fecha ?? fecha;
        setServerFechaBaja(fb);
        setServerEstado(j.estado ?? j.status ?? "BAJA");
        onHydratedStatus?.({ id, fecha_baja: fb, estado: "BAJA" });
      } else {
        setServerFechaBaja(fecha);
        setServerEstado("BAJA");
        onHydratedStatus?.({ id, fecha_baja: fecha, estado: "BAJA" });
      }
      setBajaModalOpen(false);
      onChanged?.();
    } catch (e) {
      alert(e?.message || "No se pudo marcar la baja");
    } finally {
      setSavingBaja(false);
    }
  }, [id, onChanged, onHydratedStatus]);

  const reactivarSocio = useCallback(async () => {
    setSavingBaja(true);
    try {
      const body = { fecha_baja: null, fechaBaja: null, en_baja: false, enBaja: false, estado: "ACTIVO", activo: true };
      const r = await authFetch(`/api/socios/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error("No se pudo reactivar");
      bajasLocal.clear(id);
      const r2 = await authFetch(`/api/socios/${id}`);
      if (r2.ok) {
        const j = await r2.json();
        const fb = j.fecha_baja ?? j.fechaBaja ?? j.baja_fecha ?? null;
        setServerFechaBaja(fb);
        setServerEstado(j.estado ?? j.status ?? "ACTIVO");
        onHydratedStatus?.({ id, fecha_baja: fb, estado: "ACTIVO" });
      } else {
        setServerFechaBaja(null);
        setServerEstado("ACTIVO");
        onHydratedStatus?.({ id, fecha_baja: null, estado: "ACTIVO" });
      }
      setBajaModalOpen(false);
      onChanged?.();
    } catch (e) {
      alert(e?.message || "No se pudo reactivar el socio");
    } finally {
      setSavingBaja(false);
    }
  }, [id, onChanged, onHydratedStatus]);

  /* eliminar socio */
  async function handleDelete() {
    try {
      setDeleting(true);
      await borrarSocio(id);
      setRemoving(true);
      setTimeout(() => { onDeleted?.(id); setRemoved(true); }, 220);
    } catch (e) {
      alert(e.message || "No se pudo eliminar el socio");
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setMenuOpen(false);
    }
  }

  /* estado UI derivado */
  const bajaLocalFecha = useMemo(() => {
    const loc = bajasLocal.get(id);
    return loc?.fecha || null;
  }, [id]);

  const effectiveBajaDate = serverFechaBaja ?? fecha_baja ?? bajaLocalFecha ?? null;

  const enTramiteFlag = (() => {
    if (enTramite != null) return !!enTramite;
    if (typeof pendientes === "number") return pendientes > 0;
    if (internalTramite != null) return !!internalTramite;
    return false;
  })();

  const pendientesNum = (() => {
    if (typeof pendientes === "number") return pendientes;
    if (typeof internalPend === "number") return internalPend;
    return null;
  })();

  const status = computeSocioStatus({ fecha_baja: effectiveBajaDate, enTramiteFlag, pendientesNum, estadoSrv: serverEstado });

  return (
    <>
      {/* contenedor observado */}
      <div ref={cardRef}>
        {!removed && (
          <Card
            className={`relative w-full max-w-sm p-4 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300
              ${deleting ? "opacity-50" : ""}
              ${removing ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}
              ${level === "high" ? "ring-2 ring-red-400/60 ring-offset-2 ring-offset-white motion-safe:animate-pulse motion-reduce:animate-none" : ""}
              ${level === "med" ? "ring-2 ring-amber-300/50 ring-offset-2 ring-offset-white" : ""}`}
          >
            {/* Kebab (solo empleado) */}
            {puedeAdministrar && (
              <div className="absolute top-2 right-2" ref={menuRef}>
                <button
                  className="rounded-full p-2 hover:bg-gray-100"
                  aria-label="Acciones"
                  onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                >
                  <span className="inline-block h-1 w-1 rounded-full bg-gray-700" />
                  <span className="mx-0.5 inline-block h-1 w-1 rounded-full bg-gray-700" />
                  <span className="inline-block h-1 w-1 rounded-full bg-gray-700" />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-8 z-50 w-48 overflow-hidden rounded-xl border bg-white shadow-lg">
                    <button
                      className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        onEdit?.({ id, nombre, matricula, estado: serverEstado, fecha_baja: effectiveBajaDate });
                      }}
                    >
                      Editar
                    </button>

                    <button
                      className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setBajaModalOpen(true); }}
                    >
                      {effectiveBajaDate ? "Reactivar socio" : "Dar de baja"}
                    </button>

                    <button
                      className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setConfirmOpen(true); }}
                    >
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Cuerpo card */}
            <Link to={`/socios/${id}`} className="flex items-center gap-4 pr-10 rounded-xl cursor-pointer hover:bg-gray-50">
              <img
                src={ imagen || `https://ui-avatars.com/api/?name=${encodeURIComponent(nombre || "")}&background=random&size=128` }
                alt={nombre || "Sin nombre"}
                className="w-16 h-16 rounded-full object-cover border"
                loading="lazy"
                width={64}
                height={64}
              />
              <div className="flex-1 min-w-[200px] py-2">
                <div className="text-lg font-semibold truncate">{nombre || "Nombre no disponible"}</div>
                <div className="text-sm text-gray-500 truncate">{matricula || "Sin matrícula"}</div>
                <div className="mt-1">
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      status.key === "activo" ? "bg-green-100 text-green-800"
                      : status.key === "tramite" ? "bg-indigo-100 text-indigo-800"
                      : "bg-red-100 text-red-800"
                    }`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full mr-1 ${status.dotClass}`} />
                    {status.label}
                  </Badge>
                </div>
              </div>
            </Link>

            {/* Botón Ver documentos */}
            {puedeAdministrar && (
              <div className="mt-3 flex justify-end">
                <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}>
                  <FileTextIcon className="w-4 h-4 mr-2" /> Ver documentos
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Modal Documentos */}
      {puedeAdministrar && modalOpen && (
        <ModalDocumentos isOpen={modalOpen} onClose={() => setModalOpen(false)} socio={{ id, nombre, matricula }} />
      )}

      {/* Confirmación eliminar */}
      {confirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold">Eliminar socio</h3>
            <p className="mt-2 text-sm text-gray-600">Vas a eliminar a <strong>{nombre}</strong> ({matricula}). Esta acción no se puede deshacer.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-60" onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancelar</button>
              <button className="rounded-xl bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Eliminando..." : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dar baja / Reactivar */}
      {bajaModalOpen && (
        <div className="fixed inset-0 z-[101] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !savingBaja && setBajaModalOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold">{effectiveBajaDate ? "Reactivar socio" : "Dar de baja al socio"}</h3>

            {!effectiveBajaDate ? (
              <>
                <p className="mt-2 text-sm text-gray-600">Indica la fecha efectiva de baja para <strong>{nombre}</strong>.</p>
                <div className="mt-4">
                  <label className="text-sm text-gray-700">Fecha de baja</label>
                  <input type="date" className="mt-1 w-full rounded-xl border px-3 py-2" value={bajaDateInput} onChange={(e) => setBajaDateInput(e.target.value)} min="2000-01-01" max="2100-12-31" />
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-60" onClick={() => setBajaModalOpen(false)} disabled={savingBaja}>Cancelar</button>
                  <button className="rounded-xl bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-60" onClick={() => marcarBaja(bajaDateInput)} disabled={savingBaja || !bajaDateInput} title={!bajaDateInput ? "Selecciona una fecha" : ""}>
                    {savingBaja ? "Guardando…" : "Dar de baja"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-gray-600">El socio figura con baja efectiva el <strong>{fmt(effectiveBajaDate)}</strong>. ¿Quieres reactivarlo?</p>
                <div className="mt-6 flex justify-end gap-3">
                  <button className="rounded-xl border px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-60" onClick={() => setBajaModalOpen(false)} disabled={savingBaja}>Cancelar</button>
                  <button className="rounded-xl bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-60" onClick={reactivarSocio} disabled={savingBaja}>
                    {savingBaja ? "Guardando…" : "Reactivar"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SocioCard;
